// YMM Results page — Crosslinked. v2026-05-30b
// Reads year_id / make_id / model_id (+ display labels) from the page URL.
// Calls /apps/ymm/search via the Shopify App Proxy.
// Renders self-contained product cards (.ymm-card) styled in ymm-results.liquid.
// Falls back to /products/<handle>.json for missing images, and to
// /search.json?type=product&q=<sku> for listings still missing a handle.

(function (window, document) {
  'use strict'

  var PROXY_BASE = '/apps/ymm'

  function parseParams() {
    var sp = new URLSearchParams(window.location.search)
    return {
      year_id:  sp.get('year_id')  || '',
      make_id:  sp.get('make_id')  || '',
      model_id: sp.get('model_id') || '',
      year:     sp.get('year')     || '',
      make:     sp.get('make')     || '',
      model:    sp.get('model')    || '',
    }
  }

  function isUuid(s) {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s)
  }

  // Shopify CDN supports on-the-fly resizing via ?width=N.
  // 600px = retina-ready at ~300px CSS card width (3–4 col grid).
  function shopifyCdnSrc(src, width) {
    if (!src) return src
    try {
      var url = new URL(src)
      url.searchParams.set('width', String(width))
      return url.toString()
    } catch (_) {
      return src
    }
  }

  function formatPrice(cents) {
    // API returns price as a decimal dollar amount (e.g. 49.99), NOT cents.
    if (cents === null || cents === undefined) return ''
    var n = Number(cents)
    if (isNaN(n)) return ''
    return '$' + n.toFixed(2)
  }

  function productUrl(listing) {
    if (!listing.handle) return null
    var url = '/products/' + listing.handle
    if (listing.shopify_variant_id) url += '?variant=' + listing.shopify_variant_id
    return url
  }

  // Enrich listings that still have null handle or image_src by fetching from
  // Shopify's AJAX product endpoint. For listings WITH a handle we use
  // /products/<handle>.json. For listings still missing a handle we try
  // /search.json?type=product&q=<title> (best-effort, may miss SKU-only matches).
  function enrichListings(listings) {
    var needsEnrich = listings.filter(function (l) {
      return !l.handle || !l.image_src || l.image_src.indexOf('no-image') !== -1
    })
    if (!needsEnrich.length) return Promise.resolve(listings)

    var fetches = needsEnrich.map(function (l) {
      if (l.handle) {
        // Have handle — fetch full product JSON
        return fetch('/products/' + encodeURIComponent(l.handle) + '.json', { credentials: 'same-origin' })
          .then(function (r) { return r.ok ? r.json() : null })
          .then(function (d) {
            if (!d || !d.product) return { id: l.listing_id, src: null, price: null, title: null, handle: null }
            var p = d.product
            var v = p.variants && p.variants[0]
            return {
              id: l.listing_id,
              handle: p.handle || null,
              src: p.images && p.images[0] ? p.images[0].src : null,
              price: v ? parseFloat(v.price) : null,
              title: p.title || null,
            }
          })
          .catch(function () { return { id: l.listing_id, src: null, price: null, title: null, handle: null } })
      } else if (l.name) {
        // No handle but have name — try Shopify search
        var q = encodeURIComponent(l.name.split(' ').slice(0, 4).join(' '))
        return fetch('/search.json?type=product&q=' + q, { credentials: 'same-origin' })
          .then(function (r) { return r.ok ? r.json() : null })
          .then(function (d) {
            var first = d && d.results && d.results[0]
            if (!first) return { id: l.listing_id, src: null, price: null, title: null, handle: null }
            var fi = first.featured_image
            return {
              id: l.listing_id,
              handle: first.handle || null,
              src: fi ? (typeof fi === 'string' ? fi : (fi.url || null)) : null,
              price: first.price_min ? first.price_min / 100 : null,
              title: first.title || null,
            }
          })
          .catch(function () { return { id: l.listing_id, src: null, price: null, title: null, handle: null } })
      } else {
        return Promise.resolve({ id: l.listing_id, src: null, price: null, title: null, handle: null })
      }
    })

    return Promise.all(fetches).then(function (results) {
      var byId = {}
      results.forEach(function (r) { if (r.id) byId[r.id] = r })
      return listings.map(function (l) {
        var extra = byId[l.listing_id]
        if (!extra) return l
        var merged = {}
        for (var k in l) { if (l.hasOwnProperty(k)) merged[k] = l[k] }
        if (!l.handle && extra.handle) merged.handle = extra.handle
        if ((!l.image_src || l.image_src.indexOf('no-image') !== -1) && extra.src) merged.image_src = extra.src
        if (l.price == null && extra.price != null) merged.price = extra.price
        if (!l.name && extra.title) merged.name = extra.title
        return merged
      })
    })
  }

  function renderCard(listing, index) {
    var url = productUrl(listing)

    // Outer wrapper is <a> so clicking anywhere on the card navigates to PDP —
    // matches how Impulse collection cards behave (entire card is the link).
    var card = document.createElement('a')
    card.className = 'grid__item grid-product'
    card.href = url || '#'
    if (listing.handle) card.setAttribute('data-product-handle', listing.handle)

    var content = document.createElement('div')
    content.className = 'grid-product__content'

    var imgWrapper = document.createElement('div')
    imgWrapper.className = 'grid__item-image-wrapper'

    // Image mask + square ratio container (same as Impulse's product-grid-item snippet)
    var mask = document.createElement('div')
    mask.className = 'grid-product__image-mask'

    var imgRatio = document.createElement('div')
    imgRatio.className = 'grid__image-ratio grid__image-ratio--square'

    if (listing.image_src) {
      var img = document.createElement('img')
      img.src = shopifyCdnSrc(listing.image_src, 600)
      img.alt = listing.name || ''
      img.loading = 'eager'
      if (index < 3) img.setAttribute('fetchpriority', 'high')
      imgRatio.appendChild(img)
    }
    mask.appendChild(imgRatio)
    imgWrapper.appendChild(mask)

    // Meta block — title + price, same classes as native collection cards
    var meta = document.createElement('div')
    meta.className = 'grid-product__meta'

    var titleEl = document.createElement('div')
    titleEl.className = 'grid-product__title grid-product__title--heading'
    titleEl.textContent = listing.name || ''
    meta.appendChild(titleEl)

    var priceText = formatPrice(listing.price)
    if (priceText) {
      var priceEl = document.createElement('div')
      priceEl.className = 'grid-product__price'
      priceEl.textContent = priceText
      meta.appendChild(priceEl)
    }

    imgWrapper.appendChild(meta)
    content.appendChild(imgWrapper)
    card.appendChild(content)
    return card
  }

  function show(id) {
    var el = document.getElementById(id)
    if (el) el.style.display = ''
  }

  function hide(id) {
    var el = document.getElementById(id)
    if (el) el.style.display = 'none'
  }

  function setText(id, text) {
    var el = document.getElementById(id)
    if (el) el.textContent = text
  }

  function renderListings(listings) {
    if (listings.length === 0) {
      show('ymm-results-empty')
      return
    }

    var grid = document.getElementById('ymm-results-grid')
    if (!grid) return

    enrichListings(listings).then(function (resolved) {
      var primary     = resolved.filter(function (l) { return l.product_group !== 'accessory' })
      var accessories = resolved.filter(function (l) { return l.product_group === 'accessory' })

      for (var i = 0; i < primary.length; i++) {
        grid.appendChild(renderCard(primary[i], i))
      }
      grid.classList.add('is-visible')

      if (accessories.length > 0) {
        var divider = document.createElement('div')
        divider.className = 'ymm-section-divider'
        divider.innerHTML = '<span>Accessories</span>'
        grid.parentNode.insertBefore(divider, grid.nextSibling)

        var accGrid = document.createElement('div')
        accGrid.className = 'ymm-accessories-grid is-visible'
        for (var j = 0; j < accessories.length; j++) {
          accGrid.appendChild(renderCard(accessories[j], j))
        }
        divider.parentNode.insertBefore(accGrid, divider.nextSibling)
      }

      if (window.AOS) window.AOS.refresh()
    })
  }

  function run() {
    var params = parseParams()

    if (params.year && params.make && params.model) {
      setText(
        'ymm-results-headline',
        'Parts for your ' + params.year + ' ' + params.make + ' ' + params.model
      )
      setText(
        'ymm-results-subhead',
        'All products confirmed to fit your bike, sorted by popularity.'
      )
    }

    if (!isUuid(params.year_id) || !isUuid(params.make_id) || !isUuid(params.model_id)) {
      hide('ymm-results-loading')
      show('ymm-results-error')
      setText('ymm-results-error', 'Select your bike using the bar above to see parts that fit.')
      return
    }

    var gapLink = document.getElementById('ymm-results-gap-link')
    if (gapLink) {
      gapLink.addEventListener('click', function (e) {
        e.preventDefault()
        if (window.YMM && window.YMM.openGapForm) {
          window.YMM.openGapForm({
            surface: 'header_no_results',
            year:  params.year,
            make:  params.make,
            model: params.model,
          })
        } else {
          var selector = document.querySelector('[data-ymm-selector]')
          if (selector) selector.scrollIntoView({ behavior: 'smooth' })
        }
      })
    }

    var sessionId = (function () {
      try {
        var c = document.cookie.split(';').find(function (s) { return s.trim().startsWith('ymm_session=') })
        if (c) return c.trim().slice('ymm_session='.length)
      } catch (_) {}
      var id = Math.random().toString(36).slice(2) + Date.now().toString(36)
      try { document.cookie = 'ymm_session=' + id + ';path=/;SameSite=Lax;max-age=1800' } catch (_) {}
      return id
    })()

    // Use sessionStorage cache written by ymm-widget.js (avoids double-fetch + double log).
    // NOTE: clear sessionStorage if data looks stale after a sync (open DevTools → Application →
    // Session Storage → delete ymm_search_cache).
    var cachedData = null
    try {
      var raw = sessionStorage.getItem('ymm_search_cache')
      if (raw) {
        var cached = JSON.parse(raw)
        if (
          cached &&
          cached.year_id  === params.year_id &&
          cached.make_id  === params.make_id &&
          cached.model_id === params.model_id &&
          cached.results &&
          Date.now() - cached.ts < 300000
        ) {
          // Only trust the cache when at least one listing already has an image.
          // Stale cache from before the server-side image enrichment was deployed
          // would have image_src:null for every listing — bypass it so we re-fetch.
          var listings = cached.results && cached.results.listings
          var hasSomeImages = Array.isArray(listings) && listings.some(function (l) { return !!l.image_src })
          if (hasSomeImages || !Array.isArray(listings) || listings.length === 0) {
            cachedData = cached.results
          }
        }
      }
    } catch (_) {}

    if (cachedData) {
      hide('ymm-results-loading')
      renderListings((cachedData && cachedData.listings) || [])
      return
    }

    fetch(PROXY_BASE + '/search', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        year_id:    params.year_id,
        make_id:    params.make_id,
        model_id:   params.model_id,
        session_id: sessionId,
      }),
    })
      .then(function (r) { return r.json() })
      .then(function (data) {
        hide('ymm-results-loading')
        // Write fresh data back to cache so subsequent results-page navigations
        // don't re-fetch unnecessarily (mirrors what ymm-widget.js writes).
        try {
          sessionStorage.setItem('ymm_search_cache', JSON.stringify({
            year_id: params.year_id, make_id: params.make_id, model_id: params.model_id,
            results: data, ts: Date.now(),
          }))
        } catch (_) {}
        renderListings((data && data.listings) || [])
      })
      .catch(function () {
        hide('ymm-results-loading')
        show('ymm-results-error')
      })
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run)
  } else {
    run()
  }
})(window, document)
